import React from "react";
import {
  CrudFilter,
  IResourceComponentsProps,
  useApiUrl,
  useCustom,
} from "@refinedev/core";
import { List, ShowButton } from "@refinedev/mantine";
import { useTable } from "@refinedev/react-table";
import { ColumnDef } from "@tanstack/react-table";
import {
  ActionIcon,
  Badge,
  Button,
  Flex,
  Grid,
  Group,
  LoadingOverlay,
  Pagination,
  Paper,
  ScrollArea,
  Select,
  Table,
} from "@mantine/core";
import { useSearchParams } from "react-router-dom";
import { DatePicker } from "@mantine/dates";
import { IconDownload, IconRefresh } from "@tabler/icons-react";
import { startOfDay, endOfDay } from "date-fns";
import Papa from "papaparse";
import { stringify } from "query-string";
import { generateFilter } from "@refinedev/simple-rest";
import axiosInstance from "axiosInstance";
import { CancelSession } from "entities/cancel_flow";
import { selectBrands, colorsByBrand } from "entities/cancel_flow/brand";
import { selectOutcomes, colorsByOutcome } from "entities/cancel_flow/outcome";
import AppliedFilters from "components/common/list/AppliedFilters";
import Headers from "components/common/list/Headers";
import { Rows } from "components/common/list/Rows";
import { StatCard } from "./StatCard";

// "in_progress" is not a stored outcome value (in-progress sessions have a
// NULL outcome) — the backend maps it to its in_progress scope.
const selectOutcomesWithInProgress = [
  ...selectOutcomes,
  { value: "in_progress", label: "In Progress" },
];

type SummaryData = {
  total: number;
  cancelled: number;
  deflected: number;
  abandoned: number;
  in_progress: number;
  deflection_rate: number;
  date_from: string;
  date_to: string;
};

type WatchlistSummaryData = {
  watching: number;
  saved: number;
  lost: number;
  overdue: number;
};

// Columns exported to CSV — every field the cancel_sessions list endpoint returns.
const EXPORT_FIELDS: (keyof CancelSession)[] = [
  "id",
  "uuid",
  "chargebee_customer_id",
  "chargebee_subscription_id",
  "brand",
  "plan_segment",
  "page_variant",
  "outcome",
  "started_at",
  "completed_at",
  "subscription_mrr",
  "user_id",
  "cancellation_reason_id",
  "created_at",
  "updated_at",
];

export const CancelSessionList: React.FC<IResourceComponentsProps> = () => {
  const apiUrl = useApiUrl();
  // Dashboard links pass their active filters along so this screen opens
  // showing the same data.
  const [searchParams] = useSearchParams();
  const [dateStart, setDateStart] = React.useState<Date | null>(
    searchParams.get("date_start")
      ? new Date(searchParams.get("date_start")!)
      : null,
  );
  const [dateEnd, setDateEnd] = React.useState<Date | null>(
    searchParams.get("date_end")
      ? new Date(searchParams.get("date_end")!)
      : null,
  );
  const [brand, setBrand] = React.useState<string | null>(
    searchParams.get("brand") ?? null,
  );
  const [outcome, setOutcome] = React.useState<string | null>(
    searchParams.get("outcome") ?? null,
  );
  const [isExporting, setIsExporting] = React.useState(false);

  const summaryQuery: Record<string, string> = {
    // The summary endpoints default to the last 30 days; the table shows all
    // rows when unfiltered, so anchor the range at epoch to keep them in sync.
    date_from: dateStart
      ? startOfDay(dateStart).toISOString()
      : new Date(0).toISOString(),
  };
  if (brand) summaryQuery.brand = brand;
  if (dateEnd) summaryQuery.date_to = endOfDay(dateEnd).toISOString();

  const { data: summaryData } = useCustom<SummaryData>({
    url: `${apiUrl}/cancel_dashboard/summary`,
    method: "get",
    config: { query: summaryQuery },
  });

  const { data: watchlistData } = useCustom<WatchlistSummaryData>({
    url: `${apiUrl}/cancel_dashboard/watchlist_summary`,
    method: "get",
    config: { query: summaryQuery },
  });

  const summary = summaryData?.data;
  const watchlist = watchlistData?.data;

  const columns = React.useMemo<ColumnDef<CancelSession>[]>(
    () => [
      {
        id: "id",
        accessorKey: "id",
        header: "ID",
      },
      {
        id: "uuid",
        accessorKey: "uuid",
        header: "UUID",
        cell: ({ getValue }) => {
          const value = getValue<string>();
          return value ? value.substring(0, 8) + "..." : "";
        },
      },
      {
        id: "brand",
        accessorKey: "brand",
        header: "Brand",
        cell: ({ getValue }) => {
          const value = getValue<CancelSession["brand"]>();
          return (
            <Badge variant="filled" color={colorsByBrand[value]}>
              {value}
            </Badge>
          );
        },
      },
      {
        id: "plan_segment",
        accessorKey: "plan_segment",
        header: "Segment",
      },
      {
        id: "outcome",
        accessorKey: "outcome",
        header: "Outcome",
        cell: ({ getValue }) => {
          const value = getValue<CancelSession["outcome"]>();
          if (!value) return <Badge color="gray">Pending</Badge>;
          return (
            <Badge variant="filled" color={colorsByOutcome[value]}>
              {value}
            </Badge>
          );
        },
      },
      {
        id: "subscription_mrr",
        accessorKey: "subscription_mrr",
        header: "MRR",
        cell: ({ getValue }) => {
          const value = getValue<string | null>();
          return value ? `$${value}` : "—";
        },
      },
      {
        id: "started_at",
        accessorKey: "started_at",
        header: "Started At",
        cell: ({ getValue }) => {
          const value = getValue<string>();
          return value ? new Date(value).toLocaleString() : "";
        },
      },
      {
        id: "completed_at",
        accessorKey: "completed_at",
        header: "Completed At",
        cell: ({ getValue }) => {
          const value = getValue<string>();
          return value ? new Date(value).toLocaleString() : "";
        },
      },
      {
        id: "actions",
        header: "Actions",
        cell: function render({ row: { original } }) {
          return (
            <Flex columnGap={4}>
              <ShowButton hideText recordItemId={original.id} />
            </Flex>
          );
        },
        meta: { isPlaceholder: true },
      },
    ],
    []
  );

  const {
    getHeaderGroups,
    getRowModel,
    getFlatHeaders,
    refineCore: {
      setCurrent,
      setFilters,
      pageCount,
      current,
      tableQueryResult: { isFetching, refetch },
    },
  } = useTable({
    columns,
    refineCoreProps: {
      pagination: { pageSize: 50 },
      sorters: {
        initial: [{ field: "started_at", order: "desc" }],
      },
    },
  });

  // The started_at range value the backend understands: an array that maps to a
  // SQL range (first..second). One-sided ranges are supported too.
  const startedAtRange = React.useMemo<string[] | undefined>(() => {
    if (dateStart && dateEnd)
      return [startOfDay(dateStart).toISOString(), endOfDay(dateEnd).toISOString()];
    if (dateStart) return [startOfDay(dateStart).toISOString()];
    if (dateEnd) return [new Date(0).toISOString(), endOfDay(dateEnd).toISOString()];
    return undefined;
  }, [dateStart, dateEnd]);

  // Filters shared by the live table and the CSV export so they always match.
  const activeFilters = React.useMemo<CrudFilter[]>(
    () => [
      { field: "brand", operator: "eq", value: brand || undefined },
      { field: "outcome", operator: "eq", value: outcome || undefined },
      { field: "started_at", operator: "eq", value: startedAtRange },
    ],
    [brand, outcome, startedAtRange]
  );

  React.useEffect(() => {
    setFilters(activeFilters, "merge");
    setCurrent(1);
  }, [activeFilters, setFilters, setCurrent]);

  const exportCsv = async () => {
    setIsExporting(true);
    try {
      const usedFilters = activeFilters.filter((f) => f.value !== undefined);
      const queryFilters = generateFilter(usedFilters);
      const query = {
        _start: 0,
        _end: 100000,
        _sort: "started_at",
        _order: "desc",
      };

      const url = `${apiUrl}/cancel_sessions?${stringify(query, {
        arrayFormat: "bracket",
      })}&${stringify(queryFilters, { arrayFormat: "bracket" })}`;

      const { data } = await axiosInstance.get<CancelSession[]>(url);

      const rows = data.map((row) =>
        EXPORT_FIELDS.reduce(
          (acc, field) => ({ ...acc, [field]: row[field] ?? "" }),
          {} as Record<string, unknown>
        )
      );

      const csv = Papa.unparse({ fields: EXPORT_FIELDS as string[], data: rows });
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "cancel_sessions_export.csv";
      link.click();
      URL.revokeObjectURL(link.href);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <List
      canCreate={false}
      headerButtons={
        <ActionIcon variant="subtle" onClick={() => refetch()} loading={isFetching}>
          <IconRefresh size={18} />
        </ActionIcon>
      }
    >
      {summary && watchlist && (
        <Grid mb="sm">
          <Grid.Col span={3}>
            <StatCard
              title="Saved Customers"
              value={summary.deflected}
              color="green"
            />
          </Grid.Col>
          <Grid.Col span={3}>
            <StatCard
              title="Active Watchlist"
              value={watchlist.watching}
              color="blue"
            />
          </Grid.Col>
          <Grid.Col span={3}>
            <StatCard
              title="Canceled Customers"
              value={summary.cancelled}
              color="red"
            />
          </Grid.Col>
          <Grid.Col span={3}>
            <StatCard
              title="Abandoned"
              value={summary.abandoned}
              color="gray"
            />
          </Grid.Col>
        </Grid>
      )}

      <Paper p="sm" mb="sm" withBorder>
        <Group align="flex-end" spacing="sm">
          <DatePicker
            label="Date Start"
            placeholder="Start date"
            value={dateStart}
            onChange={setDateStart}
            maxDate={dateEnd ?? undefined}
            clearable
          />
          <DatePicker
            label="Date End"
            placeholder="End date"
            value={dateEnd}
            onChange={setDateEnd}
            minDate={dateStart ?? undefined}
            clearable
          />
          <Select
            label="Brand"
            placeholder="All Brands"
            data={selectBrands}
            value={brand}
            onChange={setBrand}
            clearable
          />
          <Select
            label="Outcome"
            placeholder="All Outcomes"
            data={selectOutcomesWithInProgress}
            value={outcome}
            onChange={setOutcome}
            clearable
          />
          <Button
            leftIcon={<IconDownload size={16} />}
            onClick={exportCsv}
            loading={isExporting}
            ml="auto"
          >
            Export CSV
          </Button>
        </Group>
      </Paper>

      <AppliedFilters<CancelSession> headers={getFlatHeaders()} />
      <ScrollArea>
        <Table highlightOnHover pos="relative">
          <LoadingOverlay visible={isFetching} />
          <Headers<CancelSession> headerGroups={getHeaderGroups()} />
          <Rows<CancelSession> rows={getRowModel().rows} />
        </Table>
      </ScrollArea>
      <Pagination
        position="right"
        total={pageCount}
        page={current}
        onChange={setCurrent}
      />
    </List>
  );
};
